/** @file gizmoHit: the gizmo handle under a screen point, with the shape and placement it was found on. */
import { gizmoAccess } from './gizmoAccess';
import { gizmoFor, type GizmoPlacement } from './gizmoFor';
import { gizmoLines } from './gizmoLines';
import { GIZMO_METRICS } from './gizmoMetrics';
import type { GizmoHandle, GizmoShape } from './gizmoShape';
import { hitGizmo } from './hitGizmo';
import type { ViewportCtx } from './viewportCtx';

/** A hit: the handle, its world shape (the ring a rotate drag grabbed) and the gizmo placement. */
export type GizmoHit = { axis: GizmoHandle; shape: GizmoShape; at: GizmoPlacement };

/**
 * `hitGizmo` (within `pickRadius` CSS px) over the handles of the current tool placed by `gizmoFor` that
 * `gizmoAccess` lets it grab; null when there is no gizmo, the gizmo is locked, or no handle is under `p`.
 */
export function gizmoHit(ctx: ViewportCtx, p: readonly [number, number]): GizmoHit | null {
  const at = gizmoFor(ctx);
  if (at === null || ctx.scene === null) return null;
  const pick = gizmoAccess(ctx.store.getState(), at.tool).pick;
  const view = ctx.scene.camera.view();
  const shapes = gizmoLines(at.tool, at.model, at.size, view).filter((s) => (pick & (1 << s.axis)) !== 0);
  const axis = hitGizmo(shapes, view, p[0], p[1], GIZMO_METRICS.pickRadius * at.pxPerCss);
  const shape = shapes.find((s) => s.axis === axis);
  return axis === null || shape === undefined ? null : { axis, shape, at };
}
